import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, Eye, Pencil, Send, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const TOPIC_LABEL = {
  market: 'Market Update', buyer: 'Buyer Guide', seller: 'Seller Guide',
  property_management: 'Property Management', hoa: 'HOA Management',
};

const DraftRow = ({ post, onEdit, onPublish }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-3 py-4 border-t border-white/10" data-testid={`ai-draft-${post.id}`}>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#BCA480]">
        {post.ai_city} · {TOPIC_LABEL[post.ai_topic] || post.category}
      </p>
      <h4 className="font-serif text-lg text-[#f4efe6] truncate mt-1">{post.title}</h4>
      <p className="text-xs text-white/50 line-clamp-1">{post.excerpt}</p>
    </div>
    <div className="flex gap-2 shrink-0">
      <a href={`/blog/${post.id}`} target="_blank" rel="noreferrer" data-testid={`ai-draft-preview-${post.id}`}
        className="inline-flex items-center h-8 px-3 text-xs border border-white/20 text-white/80 hover:border-[#BCA480] hover:text-[#BCA480] transition-colors">
        <Eye className="w-3.5 h-3.5 mr-1.5" />Preview
      </a>
      <button onClick={() => onEdit(post)} data-testid={`ai-draft-edit-${post.id}`}
        className="inline-flex items-center h-8 px-3 text-xs border border-white/20 text-white/80 hover:border-[#BCA480] hover:text-[#BCA480] transition-colors">
        <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
      </button>
      <button onClick={() => onPublish(post)} data-testid={`ai-draft-publish-${post.id}`}
        className="inline-flex items-center h-8 px-3 text-xs bg-[#BCA480] text-[#1c1c1e] hover:bg-[#d1bb96] transition-colors">
        <Send className="w-3.5 h-3.5 mr-1.5" />Publish
      </button>
    </div>
  </div>
);

export const BlogAutoDrafts = ({ posts, onEdit, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);
  const drafts = posts.filter((p) => p.ai_generated && !p.published);

  const loadStatus = () => api.get('/admin/blog/autodraft-status').then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); return () => clearInterval(pollRef.current); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: job } = await api.post('/admin/blog/generate-draft');
      toast.info('Writing your draft — this takes about a minute');
      pollRef.current = setInterval(async () => {
        const { data } = await api.get(`/admin/blog/draft-jobs/${job.id}`);
        if (data.status === 'running') return;
        clearInterval(pollRef.current);
        setGenerating(false);
        if (data.status === 'done') { toast.success(`Draft ready: ${data.title}`); onChanged(); loadStatus(); }
        else toast.error(`Draft failed: ${data.error || 'unknown error'}`);
      }, 4000);
    } catch (e) {
      setGenerating(false);
      toast.error(e.response?.data?.detail || 'Could not start draft');
    }
  };

  const publish = async (post) => {
    if (!window.confirm(`Publish "${post.title}" to the live blog?`)) return;
    try {
      await api.put(`/blog/${post.id}`, { ...post, published: true });
      toast.success('Published to the blog');
      onChanged();
    } catch (e) {
      toast.error('Failed to publish');
    }
  };

  return (
    <section className="mb-6 bg-[#1c1c1e] text-[#f4efe6] p-6 md:p-8" data-testid="blog-autodrafts-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#BCA480]">Weekly AI Drafts</p>
          <h3 className="font-serif text-2xl mt-2">Your local market journal</h3>
          <p className="text-sm text-white/60 mt-1 flex items-center gap-1.5" data-testid="blog-autodraft-schedule">
            <CalendarClock className="w-4 h-4 text-[#BCA480]" />
            {status ? `${status.schedule} · Next: ${status.next_city} — ${TOPIC_LABEL[status.next_topic]}` : 'Mondays at 8:00 AM Central'}
          </p>
        </div>
        <Button onClick={generate} disabled={generating} data-testid="generate-blog-draft-btn"
          className="rounded-none bg-[#BCA480] text-[#1c1c1e] hover:bg-[#d1bb96]">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {generating ? 'Writing draft…' : 'Generate draft now'}
        </Button>
      </div>
      <div className="mt-6">
        {drafts.length === 0 ? (
          <p className="text-sm text-white/40 italic border-t border-white/10 pt-4" data-testid="ai-drafts-empty">
            No AI drafts waiting for review.
          </p>
        ) : drafts.map((p) => <DraftRow key={p.id} post={p} onEdit={onEdit} onPublish={publish} />)}
      </div>
    </section>
  );
};

export default BlogAutoDrafts;
